import React, { useContext } from 'react';
import { styled } from '@compiled/react';
import memoize from 'memoize-one';
import { useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { usePageContentId } from '@confluence/page-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { i18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import {
	SidebarContainer,
	NewReplyContainer,
	ReplyListContainer,
	EditorContainer,
} from '@confluence/inline-comments-common/entry-points/styled.ts';

import type {
	InlineCommentsFocusedInlineCommentQuery$data,
	InlineCommentsFocusedInlineCommentQuery$variables,
} from '../view-mode/__generated__/InlineCommentsFocusedInlineCommentQuery.graphql.ts';
import InlineCommentsFocusedInlineCommentQuery from '../view-mode/__generated__/InlineCommentsFocusedInlineCommentQuery.graphql.ts';
import { FOCUSED_INLINE_COMMENT_METRIC } from '../perf.config.ts';

import { CommentAuthor } from './CommentAuthor.tsx';
import { CommentHeader } from './CommentHeader.tsx';
import { CommentActions } from './CommentActions.tsx';
import { CommentRenderer } from './CommentRenderer.tsx';

// Parse content to JSON
const parseContent = memoize((content) => {
	if (!content?.length) return null;
	try {
		return JSON.parse(content);
	} catch {
		return null;
	}
});

type CommentContainerProps = {
	isFocused?: boolean;
};

type FocusedInlineCommentContainerProps = {
	showSkeleton: boolean;
	offset: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- To migrate as part of go/ui-styling-standard
const FocusedInlineCommentContainer = styled(SidebarContainer)<FocusedInlineCommentContainerProps>(`
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    top: ${(props: FocusedInlineCommentContainerProps) => props.offset}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    display: ${(props: FocusedInlineCommentContainerProps) => (props.showSkeleton ? 'block' : 'none')};
    *:not(div#ssr-comment-renderer *) {
      color: ${token('color.text.subtlest')} !important;
      cursor: not-allowed;
    }
  `);

/* Comment Editor CSS*/
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const EditorContainerSSR = styled(EditorContainer)(`
  > input {
    height: 30px;
    padding: ${token('space.050')};
    border: ${token('border.width')} solid ${token('color.border')};
    border-radius: 3px;
    box-sizing: border-box;
    font: ${token('font.body')};
    width: 100%;
  }
`);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const CommentContainer = styled.div<CommentContainerProps>(`
  display: flex;
  flex-direction: column;
  padding: ${token('space.150')} ${token('space.200')};
  border-left: 0;
  border-top: 0;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  background: ${(props: CommentContainerProps) => (props.isFocused ? token('color.background.brand.bold') : undefined)};
  blockquote {
    color: ${token('color.text')};
  }
  outline: none;

  &:last-of-type {
    border-bottom: 0;
  }`);

export const getOffset = () => {
	if (
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__ &&
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']
	) {
		return window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']['markerRefOffset'];
	}

	return 0;
};
// Makes sure to only show focusedInlineCommentSSR if the page was ssr'd
export const checkForSSRFocusedComment = () => {
	if (
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__ &&
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']
	) {
		return true;
	}

	return false;
};

export const FocusedInlineComment = () => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();

	const { match } = useContext(RoutesContext);

	const focusedCommentId = match?.query.focusedCommentId;
	const focusedCommentIdString = typeof focusedCommentId === 'string' ? focusedCommentId : '';

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data } = useQueryInterop<
		InlineCommentsFocusedInlineCommentQuery$data,
		InlineCommentsFocusedInlineCommentQuery$variables
	>(InlineCommentsFocusedInlineCommentQuery, {
		variables: { commentId: focusedCommentIdString, contentId },
		skip: !(contentId && focusedCommentId),
	});

	let commentData;

	let isTopLevelComment = true;

	/* If focusedCommentId is a reply grab top-level comment Data from ancestors.
  If ancestors is non-empty it means focusedCommentId is a reply*/
	if (data?.comments?.nodes?.[0]?.ancestors && data.comments.nodes[0].ancestors.length > 0) {
		commentData = data.comments.nodes[0].ancestors[0];
		isTopLevelComment = false;
	} else {
		commentData = data?.comments?.nodes?.[0];
	}

	if (
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__ &&
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']
	) {
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']['isTopLevelComment'] =
			isTopLevelComment;
	}

	const content = data?.content?.nodes?.[0];
	const operations = content?.operations || [];

	const canCreateComments = Boolean(
		operations.find((op) => op?.operation === 'create' && op?.targetType === 'comment'),
	);

	const topCommentUserId = commentData?.author?.accountId;
	const topCommentUserAvatar = commentData?.author?.profilePicture?.path;
	const topCommentDisplayName = commentData?.author?.displayName;
	const commentId = commentData?.id;
	const when = commentData?.version?.when;
	const commentContent = commentData?.body?.value;
	const permissionType = commentData?.author?.permissionType;

	const currentUserAvatarUrl = data?.user?.photos?.[0]?.value ?? undefined;
	const currentUserDisplayName = data?.user?.displayName ?? undefined;
	const currentUserId = data?.user?.id;

	const adf = parseContent(commentContent);

	const renderReplies = () => {
		const replies = commentData?.replies || [];

		return replies.map((reply) => {
			const adf = parseContent(reply?.body?.value);
			return (
				<CommentContainer
					key={reply?.id}
					isFocused={focusedCommentId === reply?.id}
					data-comment-id={reply?.id}
				>
					<CommentAuthor
						commentMode="view"
						userId={reply?.author?.accountId}
						avatarUrl={reply?.author?.profilePicture?.path}
						displayName={reply?.author?.displayName}
						date={reply?.version?.when}
						permissionType={reply?.author?.permissionType}
					/>
					{adf && <CommentRenderer adf={adf} />}
					<CommentActions isReply permissions={reply?.permissions} />
				</CommentContainer>
			);
		});
	};

	// Scroll to focusedInlineComment if it's out of the screen
	const scrollOffset =
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__ &&
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment'] &&
		window.__SSR_INLINE_COMMENTS_EVENTS_CAPTURE__['focusedComment']['scrollOffset'];

	if (scrollOffset) {
		window.scrollTo({
			top: scrollOffset,
		});
	}

	if (data) {
		return (
			<TransparentErrorBoundary attribution={Attribution.COLLABORATION}>
				{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
				{process.env.REACT_SSR && (
					<PageSegmentLoadStart key={`start-${contentId}`} metric={FOCUSED_INLINE_COMMENT_METRIC} />
				)}
				<FocusedInlineCommentContainer
					mode="view"
					id="focused-inline-comment-sidebar-skeleton"
					data-testid="focused-inline-comment-sidebar-skeleton"
					showSkeleton={checkForSSRFocusedComment()}
					offset={getOffset()}
				>
					<CommentHeader />
					<CommentContainer tabIndex={-1} data-comment-id={commentId}>
						<CommentAuthor
							commentMode="view"
							userId={topCommentUserId}
							avatarUrl={topCommentUserAvatar}
							displayName={topCommentDisplayName}
							date={when}
							permissionType={permissionType}
							size="medium"
						/>
						{adf && <CommentRenderer adf={adf} />}
						<CommentActions permissions={commentData?.permissions} isReply={false} />
					</CommentContainer>
					<ReplyListContainer data-testid="reply-container">{renderReplies()}</ReplyListContainer>
					{canCreateComments && (
						<NewReplyContainer mode="view">
							<EditorContainerSSR isReply mode="view">
								<CommentAuthor
									commentMode="reply"
									userId={currentUserId as string}
									avatarUrl={currentUserAvatarUrl}
									displayName={currentUserDisplayName}
									date={when}
									permissionType={permissionType}
									size="small"
								/>
								<input
									disabled
									placeholder={formatMessage(i18n.replyPlaceholder)}
									aria-label={formatMessage(i18n.replyPlaceholder)}
								/>
							</EditorContainerSSR>
						</NewReplyContainer>
					)}
				</FocusedInlineCommentContainer>
				{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
				{process.env.REACT_SSR && (
					<PageSegmentLoadEnd key={`stop-${contentId}`} metric={FOCUSED_INLINE_COMMENT_METRIC} />
				)}
			</TransparentErrorBoundary>
		);
	}

	return null;
};
